import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import {
    ActivityPointsMultiplierTierGroupUpdateRequestDto,
    ActivityType,
    WorkoutCategory,
    WorkoutTypeV2
} from '../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';
import { links } from '../../core/router';
import { useWrapRequest } from 'use-wrap-request';
import {
    ActivityPointsRuleForm,
    ActivityPointsRuleFormData,
    toActivityPointsRuleFormData,
    toOptionalPositiveNumber,
    toRequestTiers
} from './activity-points-rule-form';
import { WorkoutRecordingMethod } from './recording-method.types';

function toUpdateRequest(
    formData: ActivityPointsRuleFormData
): ActivityPointsMultiplierTierGroupUpdateRequestDto {
    return {
        activityType: formData.activityType as ActivityType,
        workoutCategory: formData.workoutCategory
            ? (formData.workoutCategory as WorkoutCategory)
            : undefined,
        workoutType:
            formData.activityType === ActivityType.WORKOUT &&
            formData.workoutType
                ? (formData.workoutType as WorkoutTypeV2)
                : undefined,
        recordingMethod:
            formData.activityType === ActivityType.WORKOUT &&
            formData.recordingMethod
                ? (formData.recordingMethod as WorkoutRecordingMethod)
                : undefined,
        requiredAverageSpeed: toOptionalPositiveNumber(
            formData.requiredAverageSpeed
        ),
        requiredCaloriesPerMinute: toOptionalPositiveNumber(
            formData.requiredCaloriesPerMinute
        ),
        priority: parseInt(formData.priority, 10),
        tiers: toRequestTiers(formData.tiers)
    };
}

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return 'Tier Group';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export const EditActivityPointsRule: React.FC = () => {
    const { ruleId } = useParams();
    const navigate = useNavigate();
    const { fitnessActivityClient } = injectTSDI(Api);

    const ruleDetails = useWrapRequest(async (id: number) => {
        const result =
            await fitnessActivityClient.admin.activityPointsMultiplierTierGroupControllerGetDetails(
                id
            );

        return result.data;
    });

    React.useEffect(() => {
        if (!ruleId) {
            return;
        }

        ruleDetails.request([parseInt(ruleId, 10)]);
    }, [ruleId]);

    const handleSubmit = async (formData: ActivityPointsRuleFormData) => {
        if (!ruleId) {
            throw new Error('Rule ID is missing.');
        }

        await fitnessActivityClient.admin.activityPointsMultiplierTierGroupControllerUpdate(
            parseInt(ruleId, 10),
            toUpdateRequest(formData)
        );

        navigate(links.activityPointsRuleDetails(ruleId));
    };

    const handleCancel = () => {
        if (ruleId) {
            navigate(links.activityPointsRuleDetails(ruleId));
            return;
        }

        navigate(links.activityPointsRules());
    };

    if (ruleDetails.loading || (!ruleDetails.result && !ruleDetails.error)) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (ruleDetails.error || !ruleDetails.result) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {ruleDetails.error?.message ||
                        'Failed to load rule details'}
                </AlertDescription>
            </Alert>
        );
    }

    const rule = ruleDetails.result;

    return (
        <ActivityPointsRuleForm
            title={`Edit ${formatEnumLabel(rule.workoutType || rule.activityType)}`}
            submitLabel="Save"
            submittingLabel="Saving..."
            initialData={toActivityPointsRuleFormData(rule)}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
        />
    );
};
